import { useNavigate } from 'react-router-dom';
import { IoHomeOutline, IoLogOutOutline, IoRefreshOutline } from 'react-icons/io5';
import { MdErrorOutline } from 'react-icons/md';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import { ReactComponent as KaiziqLogoNoTextError } from '@Assets/kaiziqLogoNoTextError.svg';
import useThemeChange from '@Hooks/useThemeChange';
import ErrorReportService from '@Helpers/URLHelpers/ErrorReportService';
import './ErrorFallBack.scss';

const ErrorFallBack = ({error}) => {

    const errorReportService = new ErrorReportService();
    const { clearCurrentProject } = useReduxProject();
    const navigate = useNavigate();
    useThemeChange();
    const className = 'errorFallback';

    const logOut = () => {
        navigate('/logout');
    }

    const handleResetErrorBoundary = () => {
        clearCurrentProject();
        navigate('/');
    }

    const handleReload = () => {
        window.location.reload();
    }

    let errorReport =
        {
            'error': error?.stack,
            'service': 'UI',
            'parentType': 'component',
            'className': error?.message,
            'function': error?.stack?.split('at ')[1]?.split(' ')[0]
        }

    errorReportService.logErrorReport(JSON.stringify(errorReport));

    return (
        <Box className={className}>
            <Box className={className + '__container'}>
                {/* Error Icon */}
                <Box className={className + '__iconWrapper'}>
                    <MdErrorOutline className={className + '__icon'} />
                </Box>

                {/* Error Message */}
                <Box className={className + '__content'}>
                    <Typography variant="h4" className={className + '__title'}>
                        Something went wrong
                    </Typography>
                    <Typography variant="body1" className={className + '__message'}>
                        We encountered an unexpected error. Our team has been notified and is working to fix this issue.
                    </Typography>

                    {/* Error Details (collapsible in production) */}
                    {process.env.NODE_ENV === 'development' && error && (
                        <Box className={className + '__details'}>
                            <Typography variant="caption" className={className + '__details__label'}>
                                Error Details:
                            </Typography>
                            <Typography variant="caption" className={className + '__details__message'}>
                                {error.message}
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Divider className={className + '__divider'} />

                {/* Action Buttons */}
                <Box className={className + '__actions'}>
                    <Button
                        variant="outlined"
                        onClick={handleReload}
                        className={className + '__button ' + className + '__button--secondary'}
                        startIcon={<IoRefreshOutline />}>
                        Reload Page
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleResetErrorBoundary}
                        className={className + '__button ' + className + '__button--primary'}
                        startIcon={<IoHomeOutline />}>
                        Go Home
                    </Button>
                </Box>

                {/* Logout Link */}
                <Box className={className + '__footer'}>
                    <Typography variant="body2" className={className + '__footer__text'}>
                        Need to start fresh?
                    </Typography>
                    <Button
                        variant="text"
                        onClick={logOut}
                        className={className + '__footer__link'}
                        startIcon={<IoLogOutOutline />}>
                        Logout
                    </Button>
                </Box>
            </Box>

            {/* Background Decoration */}
            <Box className={className + '__decoration'}>
                <Box className={className + '__decoration__circle1'} />
                <Box className={className + '__decoration__circle2'} />
                <Box className={className + '__decoration__circle3'} />
            </Box>
        </Box>
    );
}

export default ErrorFallBack;